import { FC, useState } from 'react'

import { useAtomValue } from 'jotai'
import { FieldValidationError, Switch, Flex } from '@sequencehq/core-components'
import {
  ApproversField,
  ConfigFormTemplate,
  useApprovers
} from 'Automations/nodeDefinitions/components'
import { useNode } from 'Automations/nodeDefinitions/hooks'
import { Config } from '.'
import { ConfigFormProps } from 'Automations/nodeDefinitions/types'
import { EmailForm, SendReminderSelector } from './components'
import {
  REMINDER_NODE_LINK_TEMPLATE_VARIABLES,
  REMINDER_NODE_RECIPIENT_TEMPLATE_VARIABLES,
  REMINDER_NODE_TEMPLATE_VARIABLES
} from './reminder.constants'
import { isConfigFormExpandedAtom } from 'Automations/automationEditor/atoms'

export const ReminderConfigForm: FC<ConfigFormProps> = ({ nodeId }) => {
  const { nodeData, setNodeData, definition, validationErrors } =
    useNode<Config>({ nodeId })

  const isExpanded = useAtomValue(isConfigFormExpandedAtom)

  // Drives auto-opening the approver dropdown. We only want it to open when the
  // user switches approval on — not every time the form opens for a node that
  // already requires approval — so it is tracked in component state rather than
  // derived from `isApprovalRequired`.
  const [autoOpenApprovers, setAutoOpenApprovers] = useState(false)

  const {
    userOptions,
    approvers,
    isPending: isUsersPending,
    multiSelectKey,
    removeApprover
  } = useApprovers({
    approverUserIds: nodeData?.approverUserIds ?? [],
    onChange: ids => setNodeData('approverUserIds', ids)
  })

  if (!nodeData || !definition) return null

  return (
    <ConfigFormTemplate
      width="640px"
      expandedWidth="900px"
      padding="0px"
      gap="0px"
    >
      <Flex flexDir="column" padding="8px" flex="1" overflow="hidden">
        <ConfigFormTemplate.Header
          nodeId={nodeId}
          configActions={<ConfigFormTemplate.ExpandToggle />}
        />
        <EmailForm
          to={nodeData.to}
          setTo={(value: string[]) => setNodeData('to', value)}
          cc={nodeData.cc}
          setCc={(value: string[]) => setNodeData('cc', value)}
          bcc={nodeData.bcc}
          setBcc={(value: string[]) => setNodeData('bcc', value)}
          subject={nodeData.subject}
          setSubject={(value: string) => setNodeData('subject', value)}
          body={nodeData.template}
          setBody={(value: string) => setNodeData('template', value)}
          recipientVariables={REMINDER_NODE_RECIPIENT_TEMPLATE_VARIABLES}
          subjectVariables={REMINDER_NODE_TEMPLATE_VARIABLES}
          bodyVariables={REMINDER_NODE_TEMPLATE_VARIABLES}
          bodyLinkVariables={REMINDER_NODE_LINK_TEMPLATE_VARIABLES}
          bodyHeight={isExpanded ? '100%' : '300px'}
          autoFocus={false}
          validationErrors={{
            to: validationErrors.to,
            cc: validationErrors.cc,
            bcc: validationErrors.bcc,
            subject: validationErrors.subject,
            body: validationErrors.template
          }}
        />
      </Flex>

      <ConfigFormTemplate.Separator />

      <Flex flexDir="column" padding="8px" gap="8px">
        <ConfigFormTemplate.Row label="Send reminder">
          <Flex width="100%" height="100%" justifyContent="flex-end">
            <SendReminderSelector
              value={nodeData.sendReminder}
              onChange={value => setNodeData('sendReminder', value)}
            />
          </Flex>
          {validationErrors.sendReminder?.at(0) && (
            <Flex justifyContent="flex-end" marginTop="8px">
              <FieldValidationError
                validationErrors={validationErrors.sendReminder}
                data-testid="automations.editor.configForm.reminder.sendReminder.error"
              />
            </Flex>
          )}
        </ConfigFormTemplate.Row>

        <ConfigFormTemplate.Row label="Require approval">
          <Flex width="100%" height="100%" justifyContent="flex-end">
            <Switch
              data-testid="automations.editor.configForm.reminder.isApprovalRequired"
              checked={nodeData.isApprovalRequired}
              onChange={(value: boolean) => {
                setNodeData('isApprovalRequired', value)
                // Open the approver dropdown immediately when approval is enabled
                setAutoOpenApprovers(value)
              }}
            />
          </Flex>
        </ConfigFormTemplate.Row>

        {nodeData.isApprovalRequired && (
          <ApproversField
            testIdPrefix="automations.editor.configForm.reminder"
            approvers={approvers}
            onRemoveApprover={userId => {
              // Removing an approver remounts the multi-select via
              // multiSelectKey; clear the auto-open flag so that remount does
              // not reopen the dropdown.
              setAutoOpenApprovers(false)
              removeApprover(userId)
            }}
            userOptions={userOptions}
            approverUserIds={nodeData.approverUserIds}
            isUsersPending={isUsersPending}
            multiSelectKey={multiSelectKey}
            onApproversChange={userIds =>
              setNodeData('approverUserIds', userIds)
            }
            validationErrors={validationErrors.approverUserIds}
            initiallyOpen={autoOpenApprovers}
          />
        )}
      </Flex>
    </ConfigFormTemplate>
  )
}
